import { datadogLogs } from "@datadog/browser-logs";
import { merge } from "lodash";

const SOURCE = "nodejs";
const LOG_LEVELS = ["error", "warning", "info", "debug", "alert"];
const DEFAULT_LOG_LEVEL = "info";
// List of one-line util functions
const isMap = (v) => typeof v === "object" && !Array.isArray(v) && v !== null && v !== undefined;
const isList = (v) => Array.isArray(v);
const isStr = (v) => typeof v === "string";
const stringifyTags = (v) => (isList(v) ? v.join(",") : "");
const hasTags = (v) => isList(v["tags"]);
const hasEvent = (v) => isStr(v["event"]);
const concat = (a, b) => [...(a || []), ...(b || [])];
const push = (arr, elem) => [...(arr || []), elem];

/**
 * Takes an array of arguments and extracts structured useful data
 * such as tags, event and messages.
 *
 * It also recursively inspects arrays looking for useful data.
 */
const sanitizeArgs = (args) => {
  if (!args) return {};
  return args.reduce((out, curr) => {
    if (isMap(curr)) {
      // If its a map, check if it has tags or an event property, otherwise just add props it as extra props
      if (hasTags(curr)) out["tags"] = concat(out["tags"], curr["tags"]);
      if (hasEvent(curr) && !out["event"]) out["event"] = curr["event"];
      const { tags, event, ...extra } = curr;
      out = { ...out, ...extra };
    } else if (isList(curr)) {
      // If its a list, recursively merge its elements
      out = merge(out, sanitizeArgs(curr));
      // If its not a map or an array just add it as extra messages
    } else out["extraMessages"] = push(out["extraMessages"], curr);
    return out;
  }, {});
};

/**
 * Use regex to extract the log level from a string.
 * Supported log levels are the default syslog levels
 * https://en.wikipedia.org/wiki/Syslog#Severity_level
 */
const extractLoggingLevel = (str) => {
  const lowerStr = str.toLowerCase();
  for (const level of LOG_LEVELS) {
    if (lowerStr.match(level)) return level;
  }
  return DEFAULT_LOG_LEVEL;
};

/**
 * Use the previous functions to extract and transform useful data to be sent to datadog
 */
const buildDataDogLog = (namespace, message, ...args) => {
  const { tags = [], event, ...extra } = sanitizeArgs(args);
  const level = extractLoggingLevel(namespace);
  const user = {
    userId: window.userId || "-",
    userName: window.userName || "-",
    userAgent: navigator.userAgent,
    version: window.TALENTBAIT_VERSION || "2.35"
  };
  return {
    ddsource: SOURCE,
    ddtags: stringifyTags(tags),
    level,
    message,
    namespace,
    event,
    logMessage: message,
    extra,
    env: window.env || "production",
    user
  };
};

datadogLogs.init({
  clientToken: "pub60dd7a5b8d04a6ff0438d29e2b3daec7",
  site: "datadoghq.eu",
  forwardErrorsToLogs: true,
  sampleRate: 100,
  env: window.env,
  trackSessionAcrossSubdomains: true,
  useSecureSessionCookie: true,
  useCrossSiteSessionCookie: true,
  service: "talentbait-frontend",
  silentMultipleInit: true
});

function logToDatadog(namespace, message, arg1) {
  try {
    const logMessage = buildDataDogLog(namespace, message, arg1);
    datadogLogs.logger?.[logMessage?.level](JSON.stringify(logMessage, replaceUndefinedValues));
  } catch (error) {
    // console.error("ERROR", "Logging to Datadog", error);
  }
}

function replaceUndefinedValues(key, value) {
  return value === undefined ? "undefined" : value;
}

export default logToDatadog;
